import classNames from 'classnames';
import React, { useState } from 'react';

import {
  NavigationConfigProvider,
  SplashScreenStateProvider,
  TakeoverConfigProvider,
  TranslationProvider,
  useSplashScreenState,
} from '@helios/frontend-core';
import { ThemeProvider as PalemttoComponentsThemeProvider } from '@palmetto/palmetto-components';
import { useUser } from '../../../api/user/useUser';
import { PlatformConfig } from '../../../config';
import { CurrentPropertyProvider } from '../../../contexts/CurrentPropertyContext';
import { LoadingPageProvider } from '../../LoadingPage/LoadingPage';
import ApiProvider from '../../_app/ApiProvider/ApiProvider';
import AuthProvider from '../../_app/AuthProvider/AuthProvider';
import ErrorBoundary from '../../_app/ErrorBoundary/ErrorBoundary';
import Router from '../Routes/Router';
import ThemeProvider, { getThemeFromMode } from '../ThemeProvider/ThemeProvider';
import { AnimatedSplashScreen } from './AnimatedSplashScreen';
import ApiV2ClientProvider from './ApiV2ClientProvider';

const forcedTheme = getThemeFromMode(new URLSearchParams(window.location.search).get('mode'));

const AppTranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();
  return <TranslationProvider language={user?.languageCode}>{children}</TranslationProvider>;
};

const SplashScreenAwareProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isVisible } = useSplashScreenState();
  return (
    <NavigationConfigProvider suppressViewTransitions={isVisible}>
      <TakeoverConfigProvider suppressOpen={isVisible}>{children}</TakeoverConfigProvider>
    </NavigationConfigProvider>
  );
};

const App: React.FC = () => {
  const [splashReleased, setSplashReleased] = useState(false);

  return (
    <ApiV2ClientProvider>
      <ErrorBoundary>
        <ApiProvider>
          <AuthProvider>
            <PalemttoComponentsThemeProvider>
              <ThemeProvider theme={forcedTheme}>
                <AppTranslationProvider>
                  <CurrentPropertyProvider>
                    <LoadingPageProvider>
                      <SplashScreenStateProvider>
                        <AnimatedSplashScreen onReleased={() => setSplashReleased(true)} />
                        <div
                          className={classNames('absolute h-full w-full', {
                            'motion-delay-[2.3s]/translate motion-translate-y-in-100 motion-duration-[300ms] motion-ease-[cubic-bezier(0.23, 1, 0.32, 1)]':
                              PlatformConfig.isNative && splashReleased,
                          })}
                        >
                          <div className="motion-delay-0/translate">
                            <SplashScreenAwareProviders>
                              <Router />
                            </SplashScreenAwareProviders>
                          </div>
                        </div>
                      </SplashScreenStateProvider>
                    </LoadingPageProvider>
                  </CurrentPropertyProvider>
                </AppTranslationProvider>
              </ThemeProvider>
            </PalemttoComponentsThemeProvider>
          </AuthProvider>
        </ApiProvider>
      </ErrorBoundary>
    </ApiV2ClientProvider>
  );
};

export default App;
